import get from 'lodash.get';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import getConfig from 'next/config';
import i18next from 'i18next';
import { withCookies, Cookies } from 'react-cookie';
import classNames from 'classnames';

import {
  hideProviderSelectAction,
  loadScheduleAction,
  showProviderSelectAction,
} from 'redux/modules/tve';

import {
  playmakerMetadata as PlaymakerMetadataPropType,
  playmakerTeaseStyle as PlaymakerTeaseStylePropType,
} from 'lib/CustomPropTypes';
import {
  configTempPass,
  TVE_CANONICAL_ROUTE,
  ACCESS_ENABLER_OBJ,
} from 'lib/tve';

import { ERROR_TYPES } from 'components/TVE/Slate';
import IntObserver from 'components/IntObserver';
import Player from 'components/TVE/Player';
import ProviderSelect from 'components/TVE/ProviderSelect';
import TempPass from 'components/TVE/TempPass';
import TVECountdown from 'components/TVE/Countdown';
import WithTVEAuthProvider from 'components/TVE/AuthProvider';

const {
  publicRuntimeConfig: {
    DISABLE_AUTH_TVE_MSNBC = false,
  } = {},
} = getConfig();

const block = 'playmaker-tease-msnbc';

function TeaseComponent(props) {
  const iframeRef = React.useRef(null);

  const [inView, setInView] = React.useState(false);

  const [checkTempPass, setCheckTempPass] = React.useState(false);

  const {
    authenticated,
    currentProgram,
    hasTempPass,
    hideProviderSelect,
    icid,
    loadSchedule,
    playmakerMetadata: {
      playmakerTitle,
    },
    providerSelectVisible,
    showProviderSelect,
    styleOverride,
    vertical,
    videoError,
  } = props;

  const hasAuth = authenticated || hasTempPass;

  const canonicalURL = `/${TVE_CANONICAL_ROUTE}?icid=${icid}`;

  // Actions on mount
  React.useEffect(() => {
    // Load vertical schedule
    if (!currentProgram || !currentProgram.title) {
      loadSchedule(vertical);
    }
  }, []);

  // User not authenticated with mvpd
  React.useEffect(() => {
    if (authenticated === false && hasTempPass === false) {
      // render tempPass component
      setCheckTempPass(true);
    } else if (authenticated === true && providerSelectVisible === true) {
      // Close MVPD overlay
      hideProviderSelect();
    }
  }, [authenticated]);

  const getExpiration = () => {
    const { expirationName } = configTempPass;
    const { cookies } = props;
    return parseInt(cookies.get(expirationName), 10);
  };

  const handleAction = () => {
    if (hasAuth) {
      window.location = canonicalURL;
    } else {
      showProviderSelect();
    }
  };

  return (
    <>
      {providerSelectVisible && (
        <ProviderSelect
          iframeRef={iframeRef}
          redirectUrl={`${get(window, ['location', 'origin'], '')}${canonicalURL}`}
        />
      )}
      <IntObserver
        callback={([entry]) => {
          // Component in view
          const { isIntersecting } = entry;
          if (isIntersecting) {
            setInView(true);
          }
        }}
        threshold={0}
      >
        <section className={block}>
          {inView && !DISABLE_AUTH_TVE_MSNBC && (
            <>
              <WithTVEAuthProvider
                iframeRef={iframeRef}
              />
              {videoError !== ERROR_TYPES.BROWSER && (
                <TempPass
                  checkTempPass={checkTempPass}
                />
              )}
            </>
          )}
          <div className={classNames(`${block}__grid-outer`, styleOverride.gridOuter)}>
            <div className={classNames(`${block}__grid-col1`, styleOverride.gridCol1)}>
              <div className={`${block}__player`}>
                <div className={`${block}__player-animated`}>
                  <a href={canonicalURL} className={`${block}__player-link`}>
                    {currentProgram && currentProgram.thumb && (
                      <video autoPlay loop muted playsInline>
                        <source src={currentProgram.thumb} type="video/mp4" />
                      </video>
                    )}
                  </a>
                </div>
                {hasAuth && (
                  <div className={`${block}__player-live`}>
                    <a href={canonicalURL} className={`${block}__player-link`}>
                      <Player
                        autoplayMuted
                        bypassAuth={DISABLE_AUTH_TVE_MSNBC}
                        externalControlbar={1}
                      />
                    </a>
                  </div>
                )}
                <button
                  aria-label="player action"
                  className={`${block}__player-action`}
                  onClick={handleAction}
                  type="button"
                >
                  <span
                    className={classNames(
                      `${block}__player-icon icon`,
                      {
                        'icon-lock': !authenticated && !hasTempPass,
                        'icon-volume-level-0': hasAuth,
                      },
                    )}
                  />
                </button>
              </div>
            </div>
            <div className={classNames(`${block}__grid-col2`, styleOverride.gridCol2)}>
              {hasTempPass && (
                <div className={`${block}__wrap-labels`}>
                  <a href={canonicalURL} className={`${block}__live-tag`}>
                    {i18next.t('LIVE')}
                  </a>
                  <div className={`${block}__label`}>
                    {i18next.t('Daily preview expires in')}
                    &nbsp;
                    <span className={`${block}__countdown`}>
                      <TVECountdown
                        end={getExpiration()}
                        onEndCallback={() => {
                          if (
                            typeof window !== 'undefined'
                            && typeof window[ACCESS_ENABLER_OBJ] !== 'undefined'
                            && typeof window[ACCESS_ENABLER_OBJ].logout === 'function'
                          ) {
                            window[ACCESS_ENABLER_OBJ].logout();
                          }
                        }}
                      />
                    </span>
                  </div>
                </div>
              )}
              <div className={`${block}__wrap-tags`}>
                {!hasTempPass && (
                  <a href={canonicalURL} className={`${block}__live-tag`}>
                    {i18next.t('LIVE')}
                  </a>
                )}
                <a href={canonicalURL} className={`${block}__logo-tag`}>
                  <span className="icon icon-msnbc-tv" />
                </a>
              </div>
              {(playmakerTitle || (currentProgram && currentProgram.title)) && (
                <h3 className={`${block}__title`}>
                  <a href={canonicalURL}>
                    {playmakerTitle || currentProgram.title}
                  </a>
                </h3>
              )}
            </div>
          </div>
        </section>
      </IntObserver>
    </>
  );
}

TeaseComponent.propTypes = {
  authenticated: PropTypes.bool,
  cookies: PropTypes.instanceOf(Cookies).isRequired,
  currentProgram: PropTypes.shape({
    title: PropTypes.string,
    thumb: PropTypes.string,
  }),
  hasTempPass: PropTypes.bool,
  hideProviderSelect: PropTypes.func,
  icid: PropTypes.string,
  loadSchedule: PropTypes.func,
  providerSelectVisible: PropTypes.bool,
  playmakerMetadata: PlaymakerMetadataPropType.isRequired,
  showProviderSelect: PropTypes.func,
  styleOverride: PlaymakerTeaseStylePropType.isRequired,
  vertical: PropTypes.string.isRequired,
  videoError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
};

TeaseComponent.defaultProps = {
  authenticated: null,
  currentProgram: {},
  hasTempPass: false,
  hideProviderSelect: Function.prototype,
  icid: null,
  loadSchedule: Function.prototype,
  providerSelectVisible: false,
  showProviderSelect: Function.prototype,
  videoError: false,
};

const mapStateToProps = ({ tve, video }) => ({
  authenticated: tve.authenticated,
  currentProgram: {
    title: get(tve, ['epgCurrent', 'program', 'title']),
    thumb: get(tve, ['epgCurrent', 'thumbMd']),
  },
  hasTempPass: tve.hasTempPass,
  providerSelectVisible: tve.providerSelectVisible,
  videoError: video.error,
});

const mapActionsToProps = (dispatch) => ({
  hideProviderSelect: () => dispatch(hideProviderSelectAction()),
  loadSchedule: (vertical) => dispatch(loadScheduleAction(vertical)),
  showProviderSelect: () => dispatch(showProviderSelectAction()),
});

const PlaymakerTeaseMSNBC = connect(mapStateToProps, mapActionsToProps)(
  withCookies(TeaseComponent),
);

export { PlaymakerTeaseMSNBC };
